import ClientCreate from "components/ClientCreate";
import ClientEditPopup from "components/ClientEditPopup";
import React, { useState } from "react";
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { cognitoUserPoolId } from "services/api";
import {
  clientBoardingActivity,
  createClient,
  fetchClientList
} from "services/custom-features";
import { Divider, TableContainer } from "@mui/material";
import Loader from "components/Loader";
import { store } from "services/state";
import { observer } from "mobx-react-lite";

const scrollToTopOfPage = () => {
  document.getElementById('page-with-sidebar-content').scrollTo(0, 0);
}

const ClientBoarding = observer(() => {
  const [clientEnv, setClientEnv] = useState("");
  const [clientList, setClientList] = useState([]);
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [isSelectedClientEnv, setIsSelectedClientEnv] = useState(true);

  const envList = store.environmentList;
  const loading = envList.length === 0;

  const defaultWebhookMap = JSON.stringify({
    defaultUrl: {
      "url": "",
      "method": "POST"
    },
    updateFieldTask: {},
    updateWorklog: {},
    bulkUploadTask: {},
    startTravelTime: {},
    stopTravelTime: {},
    cancelTravelTime: {},
    onsiteTravelTime: {},
    releaseTravelTime: {},
    updateFacilityRequest: {},
    takeFieldTask: {},
    takefieldtaskandtravel: {},
    createFieldTask: {}
  }, null, 2);

  const defaultHeaders = JSON.stringify({}, null, 2);

  const [formData, setFormData] = useState({
    clientName: "",
    clientEmail: "",
    env: "",
    webhookAuthType: "",
    webhookAuthId: "",
    webhookAuthSecret: "",
    webhookContentType: "",
    webhookGrantType: "",
    webhookScope: "",
    webhookTokenUrl: "",
    webhooksMap: defaultWebhookMap,
    headers: defaultHeaders
  });

  const [notification, setNotification] = useState({
    responseType: "",
    showMessage: false,
    message: "",
  });

  const createClientOnboarding = async () => {
    let {
      clientName,
      clientEmail,
      env,
      webhookAuthType,
      webhookAuthId,
      webhookAuthSecret,
      webhookContentType,
      webhookGrantType,
      webhookScope,
      webhookTokenUrl,
      webhooksMap,
    } = formData;
    const newErrors = {};

    if (!clientName) {
      newErrors.clientName = "Client name is required**";
    }

    if (!clientEmail) {
      newErrors.clientEmail = "Client email is required**";
    }

    if (!env) newErrors.env = "Environment is required";

    if (!webhooksMap) {
      newErrors.webhooksMap = "Webhooks JSON is required";
    } else {
      try {
        JSON.parse(webhooksMap);
      } catch (e) {
        newErrors.webhooksMap = "Invalid JSON format";
      }
    }

    if (webhookAuthType) {
      if (!webhookAuthId)
        newErrors.webhookAuthId = "Webhook Username is required**";
      if (!webhookAuthSecret)
        newErrors.webhookAuthSecret = "Webhook Password is required**";
      if (webhookAuthType === "OAUTH2" && !webhookTokenUrl)
        newErrors.webhookTokenUrl = "Webhook Token URL is required**";
    }

    if (!webhookAuthType) {
      webhookAuthId = "";
      webhookAuthSecret = "";
      webhookContentType = "";
      webhookGrantType = "";
      webhookTokenUrl = "";
      webhookScope = "";
    }
    if (Object.keys(newErrors).length === 0) {
      const response = await createClient({
        ...formData,
        userPoolId: cognitoUserPoolId,
      });

      setNotification({
        status: response.status,
        showMessage: true,
        message: response.message,
      });
      scrollToTopOfPage();

      setFormData({
        clientName: "",
        clientEmail: "",
        env: "",
        webhookAuthType: "",
        webhookAuthId: "",
        webhookAuthSecret: "",
        webhookContentType: "",
        webhookGrantType: "",
        webhookScope: "",
        webhookTokenUrl: "",
        webhooksMap: defaultWebhookMap,
        headers: defaultHeaders
      });
      setErrors({});
    } else {
      // Display validation errors
      setErrors(newErrors);
    }
  };

  const showClientListByEnv = async (event, formSubmit = true) => {
    formSubmit && event.preventDefault();
    try {
      if (clientEnv) {
        const response = await fetchClientList(clientEnv);

        //Show notification only when form is submitted
        if(formSubmit) {
          setNotification({
            status: response.status,
            showMessage: true,
            message: response.message,
          });
        }
        setIsSelectedClientEnv(true);
        setClientList(response.data);
      } else {
        setIsSelectedClientEnv(false);
      }
    } catch (error) {
      setNotification({
        status: "500",
        showMessage: true,
        message: error.message,
      });
    }
  };

  const handleDismiss = () => {
    setNotification({
      status: "",
      showMessage: false,
      message: "",
    });
  };
  const [selectedClientForEdit, setSelectedClientForEdit] = useState(null);

  const handleClientEdit = (client) => {
    setSelectedClientForEdit(client);
    setOpen(!open);
  };

  const handleClientActivity = async (client) => {
    try {
      const clientBoardingStatus = await clientBoardingActivity({
        clientEnv,
        clientId: client.client_id,
        clientStatus: !client.active,
      });
      const clientListResponse = await fetchClientList(clientEnv);
      setNotification({
        status: clientBoardingStatus.status,
        showMessage: true,
        message: clientBoardingStatus.message,
      });
      setIsSelectedClientEnv(true);
      setClientList(clientListResponse.data);
      scrollToTopOfPage();
    } catch (error) {
      setNotification({
        status: "500",
        showMessage: true,
        message: error.message || "Error while updating client activity status",
      });
    }
  };

  return (
    <>
    {loading ? <Loader /> :
    <Container maxWidth={false} sx={{ p: 2 }}>
        {notification.showMessage ? (
          <Alert severity={notification.status === 200 ? 'success' : 'error'} onClose={handleDismiss} sx={{  mb: 2 }}>
            {notification.message}
          </Alert>
        ) : null}
        <Typography variant="h5" gutterBottom>
          Client Onboarding
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <ClientCreate
          setNotification={setNotification}
          formData={formData}
          setFormData={setFormData}
          createClientOnboarding={createClientOnboarding}
          errors={errors}
          isEdit={false}
        />
        <Box sx={{ pt: 6 }}>
          <Typography variant="h5" gutterBottom>Client List</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box component="form" onSubmit={showClientListByEnv} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 300 }} error={!isSelectedClientEnv} size="small">
              <InputLabel id="client-env-label">Environment</InputLabel>
              <Select
                labelId="client-env-label"
                name="clientEnv"
                required
                value={clientEnv}
                label="Environment"
                onChange={(e) => { setClientEnv(e.target.value); setIsSelectedClientEnv(true); }}
              >
                {envList.map(opt => (
                  <MenuItem key={opt.key} value={opt.value}>{opt.text}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button disableElevation type="submit" variant="contained">Submit</Button>
          </Box>
          <TableContainer>
            <Table 
            sx={{ 
              mt: 4,
              '& .MuiTableCell-head': {
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5'
              } 
            }} >
            <TableHead>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell>Client Email</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientList.length ? clientList.map((client) => (
                <TableRow key={client.client_id}>
                  <TableCell>{client.client_name}</TableCell>
                  <TableCell>{client.client_email}</TableCell>
                  <TableCell>
                    {client.active && (
                      <Button disableElevation sx={{ mr: 1 }} variant="outlined" onClick={() => handleClientEdit(client)}>Edit</Button>
                    )}
                    <Button disableElevation variant="contained" color={client.active ? 'error' : 'success'} onClick={() => handleClientActivity(client)}>
                      {client.active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6, fontSize: 20 }}>No Data available!</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
        </Box>
    </Container>
    }
      <ClientEditPopup
        handler={handleClientEdit}
        open={open}
        setNotification={setNotification}
        client={selectedClientForEdit}
        clientEnv={clientEnv}
        showClientListByEnv={showClientListByEnv}
        scrollToTopOfPage={scrollToTopOfPage}
      />
    </>
  );
});

export default ClientBoarding;
